import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="space-y-6">
              <header>
                <h1 className="text-xl font-bold uppercase tracking-widest">Error</h1>
                <p className="mt-2 text-sm text-gray-600">Something unexpected happened</p>
              </header>

              <div className="border border-black p-6 bg-white">
                <p className="text-sm mb-6">
                  An unexpected error occurred while rendering this page.
                  You can try again or refresh the page to start over.
                </p>

                <div className="flex gap-4 text-xs font-mono">
                  <button
                    onClick={this.handleRetry}
                    className="hover:underline"
                  >
                    [ try again ]
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="hover:underline text-gray-500"
                  >
                    [ refresh page ]
                  </button>
                  <a
                    href="/"
                    className="hover:underline text-gray-500"
                  >
                    [ go home ]
                  </a>
                </div>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-xs font-mono text-gray-500 hover:text-black">
                      [ show error details ]
                    </summary>
                    <pre className="mt-4 p-4 bg-gray-50 border border-gray-200 text-xs overflow-auto font-mono">
                      {this.state.error.toString()}
                      {this.state.error.stack && `\n\n${this.state.error.stack}`}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
